import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "@/lib/usePermissions";
import {
  useGetHostProfile,
  useCreateHostProfile,
  useUpdateHostProfile,
  useGetStrutture,
  syncHostStruttura,
} from "@/lib/supabaseQueries";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Camera, CheckCircle } from "lucide-react";

const ACCENT = "#c87045";
const TABS = ["Informazioni base", "Profilo", "Interessi", "Statistiche", "Foto"];
const RUOLI_HOST = [
  "Host",
  "Proprietario",
  "Property Manager",
  "Responsabile Operativo",
];
const LINGUE = ["Italiano", "English", "Français", "Deutsch", "Español", "Português", "Русский", "中文", "العربية", "日本語"];
const INTERESSI: { label: string; emoji: string; voci: { emoji: string; nome: string }[] }[] = [
  { label: "NATURA E AVVENTURA", emoji: "🌿", voci: [{ emoji: "🏔️", nome: "Trekking" }, { emoji: "🏄", nome: "Surf" }, { emoji: "🚵", nome: "Ciclismo" }, { emoji: "🐾", nome: "Animali" }, { emoji: "🌍", nome: "Culture" }] },
  { label: "SPORT E BENESSERE", emoji: "💪", voci: [{ emoji: "🧘", nome: "Yoga" }, { emoji: "🏊", nome: "Nuoto" }, { emoji: "🏃", nome: "Running" }] },
  { label: "ARTE E CREATIVITÀ", emoji: "🎨", voci: [{ emoji: "🎨", nome: "Arte" }, { emoji: "📷", nome: "Fotografia" }, { emoji: "🎸", nome: "Chitarra" }, { emoji: "🧩", nome: "Puzzle" }] },
  { label: "CULTURA E INTRATTENIMENTO", emoji: "🎭", voci: [{ emoji: "🎵", nome: "Musica" }, { emoji: "📚", nome: "Lettura" }, { emoji: "🎭", nome: "Teatro" }, { emoji: "🍷", nome: "Vino" }] },
  { label: "CIBO E STILE DI VITA", emoji: "🔍", voci: [{ emoji: "🔍", nome: "Cucina" }, { emoji: "🍕", nome: "Street Food" }] },
  { label: "VIAGGI ED ESPLORAZIONI", emoji: "✈️", voci: [{ emoji: "✈️", nome: "Viaggi" }] },
];

function parseJson(s: any, fallback: any = []) {
  try { return JSON.parse(s || JSON.stringify(fallback)); } catch { return fallback; }
}

const TEMPI_RISPOSTA = ["Entro 1 ora", "Entro poche ore", "Entro 24 ore", "Entro 2 giorni"];

const PAESI = [
  "Italia", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Belgio", "Brasile",
  "Canada", "Cina", "Colombia", "Croazia", "Danimarca", "Egitto", "Emirati Arabi Uniti",
  "Filippine", "Finlandia", "Francia", "Germania", "Giappone", "Grecia", "India", "Irlanda",
  "Israele", "Marocco", "Messico", "Norvegia", "Paesi Bassi", "Polonia", "Portogallo",
  "Regno Unito", "Romania", "Russia", "Slovacchia", "Slovenia", "Spagna", "Svezia",
  "Svizzera", "Tunisia", "Turchia", "Ucraina", "Ungheria", "USA", "Altro",
];

const REGIONI_ITALIA = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna", "Friuli-Venezia Giulia",
  "Lazio", "Liguria", "Lombardia", "Marche", "Molise", "Piemonte", "Puglia", "Sardegna",
  "Sicilia", "Toscana", "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];

const emptyForm = {
  nome_display: "", email: "", ruolo: "Host", paese: "Italia", regione: "",
  telefono: "", email_contatto: "", whatsapp: "",
  bio: "", lingue: "[]", interessi: "[]",
  strutture_ids: "[]", strutture_nomi: "[]",
  tempo_risposta: 0,
  avatar: "", foto_copertina: "",
  verificato: false, profile_boost: false,
  punteggio: 0, num_recensioni: 0,
};

function PhotoUpload({ label, value, onChange, round }: { label: string; value: string; onChange: (b64: string) => void; round?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = round ? 400 : 1200;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else { width = Math.round(width * MAX / height); height = MAX; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d")!.drawImage(img, 0, 0, width, height);
      onChange(canvas.toDataURL("image/jpeg", 0.78));
    };
    img.src = url;
  };
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
      <div className="flex items-center gap-4">
        {value ? (
          <img src={value} alt="" className={`w-20 h-20 object-cover ${round ? "rounded-full" : "rounded-xl"}`} />
        ) : (
          <div className={`w-20 h-20 bg-muted flex items-center justify-center ${round ? "rounded-full" : "rounded-xl"}`}>
            <Camera className="w-6 h-6 text-muted-foreground/40" />
          </div>
        )}
        <div>
          <button type="button" onClick={() => inputRef.current?.click()}
            className="text-sm font-medium flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border hover:border-primary hover:text-primary transition-colors"
            style={{ color: "#6b6b6b" }}>
            <Camera className="w-3.5 h-3.5" /> Carica foto
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden"
            onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
          {value && <button type="button" onClick={() => onChange("")} className="block mt-1 text-xs text-red-400 hover:text-red-500">Rimuovi</button>}
        </div>
      </div>
    </div>
  );
}

export default function HostPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === "nuovo";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing, isLoading: loadingHost } = useGetHostProfile(isNew ? "" : (id ?? ""), { query: { enabled: !isNew && !!id, queryKey: ['host-profile', id] } });
  const { data: strutture = [] } = useGetStrutture();
  const { isAdmin } = usePermissions();
  const create = useCreateHostProfile();
  const update = useUpdateHostProfile();

  const [tab, setTab] = useState(0);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (existing && !isNew) {
      setForm({
        nome_display: (existing as any).nome_display ?? "",
        email: (existing as any).email ?? "",
        ruolo: (existing as any).ruolo ?? "Solo Host",
        paese: (existing as any).paese ?? "Italia",
        regione: (existing as any).regione ?? "",
        telefono: (existing as any).telefono ?? "",
        email_contatto: (existing as any).email_contatto ?? "",
        whatsapp: (existing as any).whatsapp ?? "",
        bio: (existing as any).bio ?? "",
        lingue: (existing as any).lingue ?? "[]",
        interessi: (existing as any).interessi ?? "[]",
        strutture_ids: (existing as any).strutture_ids ?? "[]",
        strutture_nomi: (existing as any).strutture_nomi ?? "[]",
        avatar: (existing as any).avatar ?? "",
        foto_copertina: (existing as any).foto_copertina ?? "",
        verificato: (existing as any).verificato ?? false,
        profile_boost: (existing as any).profile_boost ?? false,
        punteggio: (existing as any).punteggio ?? 0,
        num_recensioni: (existing as any).num_recensioni ?? 0,
        tempo_risposta: (existing as any).tempo_risposta ?? 0,
      });
    }
  }, [existing, isNew]);

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const lingue: string[] = parseJson(form.lingue);
  const interessi: string[] = parseJson(form.interessi);
  const strutture_ids: number[] = parseJson(form.strutture_ids, []).map(Number);

  const toggleLingua = (l: string) => set("lingue", JSON.stringify(lingue.includes(l) ? lingue.filter(x => x !== l) : [...lingue, l]));
  const toggleInteresse = (nome: string) => set("interessi", JSON.stringify(interessi.includes(nome) ? interessi.filter(x => x !== nome) : [...interessi, nome]));
  const toggleStruttura = (s: any) => {
    const currNomi: string[] = parseJson(form.strutture_nomi, []);
    if (strutture_ids.includes(s.id)) {
      const i = strutture_ids.indexOf(s.id);
      set("strutture_ids", JSON.stringify(strutture_ids.filter(x => x !== s.id)));
      set("strutture_nomi", JSON.stringify(currNomi.filter((_, idx) => idx !== i)));
    } else {
      set("strutture_ids", JSON.stringify([...strutture_ids, s.id]));
      set("strutture_nomi", JSON.stringify([...currNomi, s.nome]));
    }
  };

  const handleSave = () => {
    if (!form.nome_display || !isAdmin) return;
    setSaveError(null);
    const opts = {
      onSuccess: async (res: any) => {
        const host_id = isNew ? res?.id : id!;
        // Sincronizza strutture.host_id per ogni struttura assegnata
        if (host_id) {
          const newIds: string[] = (() => { try { return JSON.parse(form.strutture_ids || "[]"); } catch { return []; } })();
          const oldIds: string[] = (() => { try { return JSON.parse((existing as any)?.strutture_ids || "[]"); } catch { return []; } })();
          // Strutture rimosse: resetta host_id
          const removed = oldIds.filter((sid: string) => !newIds.includes(sid));
          for (const sid of removed) {
            try { await syncHostStruttura({ struttura_id: sid, new_host_id: null, old_host_id: host_id }); } catch (_) {}
          }
          // Strutture aggiunte: assegna host_id
          const added = newIds.filter((sid: string) => !oldIds.includes(sid));
          for (const sid of added) {
            try { await syncHostStruttura({ struttura_id: sid, new_host_id: host_id, old_host_id: null }); } catch (_) {}
          }
        }
        qc.invalidateQueries({ queryKey: ["host-profiles"] });
        qc.invalidateQueries({ queryKey: ["host-profile", id] });
        qc.invalidateQueries({ queryKey: ["strutture"] });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        if (isNew && res?.id) navigate(`/team/host/${res.id}`, { replace: true });
      },
      onError: (err: any) => setSaveError(err?.message ?? "Errore durante il salvataggio"),
    };
    const data = { ...form, tempo_risposta: Number(form.tempo_risposta) || 0 };
    if (isNew) create.mutate(data, opts);
    else update.mutate({ id: id!, data }, opts as any);
  };

  const isPending = create.isPending || update.isPending;

  const inp = "w-full border border-border rounded-xl px-3.5 py-2.5 text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors";
  const lbl = "block text-sm font-medium text-foreground mb-1.5";

  if (!isNew && loadingHost) {
    return <div className="p-6 text-sm text-muted-foreground">Caricamento...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/team/host")}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-border hover:border-primary hover:text-primary transition-colors text-muted-foreground">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-foreground">{isNew ? "Nuovo host" : form.nome_display || "Modifica host"}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{isNew ? "Crea un nuovo profilo host" : "Modifica il profilo"}</p>
        </div>
      </div>

      {saveError && (
        <div className="mb-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{saveError}</div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-border mb-6 overflow-x-auto">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            className="py-3 px-4 text-sm font-medium whitespace-nowrap flex-shrink-0 transition-colors"
            style={{ color: tab === i ? "#1a1814" : "#9a9a9a", borderBottom: tab === i ? "2px solid #1a1814" : "2px solid transparent" }}>
            {t}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-5">

        {tab === 0 && (
          <>
            <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Informazioni base</h3>
              <div>
                <label className={lbl}>Nome display *</label>
                <input className={inp} value={form.nome_display} onChange={e => set("nome_display", e.target.value)} placeholder="Es. Marco Rossi" />
              </div>
              <div>
                <label className={lbl}>Email *</label>
                <input className={inp} type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="nome@email.com" />
              </div>
              <div>
                <label className={lbl}>Ruolo</label>
                <div className="flex flex-wrap gap-2">
                  {RUOLI_HOST.map(r => {
                    const sel = form.ruolo === r;
                    return (
                      <button key={r} type="button" onClick={() => set("ruolo", r)}
                        className="px-3 py-2 rounded-xl text-sm font-medium border transition-all"
                        style={{ borderColor: sel ? "#1a1814" : "#e8e3dc", backgroundColor: sel ? "#1a1814" : "white", color: sel ? "white" : "#6b6b6b" }}>
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Paese</label>
                  <select className={inp} value={form.paese} onChange={e => { set("paese", e.target.value); set("regione", ""); }}>
                    {PAESI.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Regione</label>
                  {form.paese === "Italia" ? (
                    <select className={inp} value={form.regione} onChange={e => set("regione", e.target.value)}>
                      <option value="">Seleziona regione</option>
                      {REGIONI_ITALIA.map(r => <option key={r}>{r}</option>)}
                    </select>
                  ) : (
                    <input className={inp} value={form.regione} onChange={e => set("regione", e.target.value)} placeholder={form.paese && form.paese !== "Altro" ? form.paese : "Es. Catalogna"} />
                  )}
                </div>
              </div>
            </div>

            <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Contatti</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Telefono</label>
                  <input className={inp} value={form.telefono} onChange={e => set("telefono", e.target.value)} placeholder="+39 333 000 0000" />
                </div>
                <div>
                  <label className={lbl}>WhatsApp</label>
                  <input className={inp} value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)} placeholder="+39 333 000 0000" />
                </div>
              </div>
              <div>
                <label className={lbl}>Email di contatto alternativa</label>
                <input className={inp} type="email" value={form.email_contatto} onChange={e => set("email_contatto", e.target.value)} placeholder="alternativa@email.com" />
              </div>
            </div>

            <div className="bg-card rounded-2xl border border-border p-5 space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Strutture assegnate</h3>
              {(strutture as any[]).length === 0 && <p className="text-sm text-muted-foreground">Nessuna struttura disponibile</p>}
              <div className="flex flex-wrap gap-2">
                {(strutture as any[]).map((s: any) => {
                  const checked = strutture_ids.includes(s.id);
                  return (
                    <button key={s.id} type="button" onClick={() => toggleStruttura(s)}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all"
                      style={{ borderColor: checked ? ACCENT : "#e8e3dc", backgroundColor: checked ? "#fdf6f1" : "white", color: checked ? ACCENT : "#6b6b6b" }}>
                      <span className="text-base">🏠</span>
                      <span>{s.nome}</span>
                      {s.paese && <span className="text-xs opacity-60">· {s.paese || s.citta}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

          </>
        )}

        {tab === 1 && (
          <div className="bg-card rounded-2xl border border-border p-5 space-y-5">
            <h3 className="text-sm font-semibold text-foreground">Profilo pubblico</h3>
            <div>
              <label className={lbl}>Bio</label>
              <textarea className={inp + " resize-none"} rows={6}
                value={form.bio} onChange={e => set("bio", e.target.value)}
                placeholder="Racconta chi sei come host..." />
            </div>
            <div>
              <label className={lbl}>Lingue parlate</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {LINGUE.map(l => {
                  const sel = lingue.includes(l);
                  return (
                    <button key={l} type="button" onClick={() => toggleLingua(l)}
                      className="px-3 py-1.5 rounded-full text-sm border transition-all"
                      style={{ borderColor: sel ? ACCENT : "#e8e3dc", backgroundColor: sel ? "#fdf6f1" : "white", color: sel ? ACCENT : "#6b6b6b" }}>
                      {sel && "✓ "}{l}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {tab === 2 && (
          <div className="bg-card rounded-2xl border border-border p-5 space-y-5">
            <h3 className="text-sm font-semibold text-foreground">Interessi & Passioni</h3>
            <p className="text-xs text-muted-foreground">Appariranno nella Guest App per far conoscere l'host agli ospiti.</p>
            {INTERESSI.map(cat => (
              <div key={cat.label}>
                <h4 className="text-xs font-bold text-foreground mb-2">{cat.emoji} {cat.label}</h4>
                <div className="grid grid-cols-2 gap-2">
                  {cat.voci.map(v => {
                    const sel = interessi.includes(v.nome);
                    return (
                      <button key={v.nome} type="button" onClick={() => toggleInteresse(v.nome)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all text-left"
                        style={{ borderColor: sel ? ACCENT : "#e8e3dc", backgroundColor: sel ? "#fdf6f1" : "white", color: sel ? ACCENT : "#6b6b6b" }}>
                        <div className="w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center"
                          style={{ borderColor: sel ? ACCENT : "#d1ccc6", backgroundColor: sel ? ACCENT : "white" }}>
                          {sel && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                        </div>
                        {v.emoji} {v.nome}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 3 && (
          <div className="space-y-3">
            <div className="bg-card rounded-2xl border border-border p-5">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-3">Statistiche</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 rounded-xl bg-yellow-50 border border-yellow-200 text-center">
                  <p className="text-xs font-bold text-yellow-800 mb-1">⭐ Punteggio</p>
                  <p className="text-2xl font-bold text-yellow-900">{form.punteggio || "—"}</p>
                  <p className="text-xs text-yellow-700">su 5 stelle</p>
                </div>
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-center">
                  <p className="text-xs font-bold text-amber-800 mb-1">📊 Recensioni</p>
                  <p className="text-2xl font-bold text-amber-950">{form.num_recensioni}</p>
                  <p className="text-xs text-amber-800">totali ricevute</p>
                </div>
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-center">
                  <p className="text-xs font-bold text-blue-800 mb-1">⚡ Risposta</p>
                  <p className="text-sm font-bold text-blue-900 leading-tight mt-1">{form.tempo_risposta || "—"}</p>
                  <p className="text-xs text-blue-700 mt-1">tempo medio</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">📌 Questi dati si aggiornano automaticamente dalle recensioni della Guest App.</p>
            </div>
          </div>
        )}

        {tab === 4 && (
          <div className="bg-card rounded-2xl border border-border p-5 space-y-6">
            <h3 className="text-sm font-semibold text-foreground">Foto del profilo</h3>
            <PhotoUpload label="Foto profilo (avatar)" value={form.avatar} onChange={v => set("avatar", v)} round />
            <div className="border-t border-border pt-5">
              <PhotoUpload label="Foto di copertina" value={form.foto_copertina} onChange={v => set("foto_copertina", v)} />
            </div>
          </div>
        )}


        {/* Save button */}
        <div className="pb-8">
          <button onClick={handleSave} disabled={isPending || !form.nome_display || !isAdmin}
            className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {saved ? <><CheckCircle className="w-4 h-4" /> Salvato!</> : isPending ? "Salvataggio..." : "Salva modifiche"}
          </button>
        </div>
      </div>
    </div>
  );
}
